import { XMLBuilder } from "fast-xml-parser";
import { supportedLocales } from "@/lib/i18n/config";
import { publicJobId } from "@/lib/jobs/public-id";
import { slugify } from "@/lib/utils/format";

const BASE_URL = "https://lokerdollar.com";
const X_DEFAULT_LOCALE = "id";

/**
 * Shared XMLBuilder instance for the fragments below (hreflang links, a
 * `<url>` entry, a `<sitemap>` index entry). `format: true` / `indentBy`
 * keep the output human-inspectable; `suppressEmptyNode` self-closes the
 * attribute-only `<xhtml:link>` elements.
 *
 * Formatting note (P6.4, reviewed): the previous hand-rolled string-concat
 * hardcoded the INDENT DEPTH assuming each fragment would be spliced into a
 * specific position inside a manually-indented parent document (4sp for
 * `<url>`, 6sp for its children). XMLBuilder has no knowledge of that
 * external nesting, so each fragment now starts at its own 0-indent and the
 * composed document's indentation is shallower than before. This is a
 * READABILITY-ONLY difference — sitemap/feed consumers (Google, LinkedIn,
 * RSS readers) do not treat inter-element whitespace as significant. Every
 * `<loc>`/`<lastmod>` TEXT leaf still renders inline on one line
 * (`<loc>...</loc>`, no internal whitespace), which is the part the test
 * suite (`toContain("<loc>...</loc>")`) actually depends on — verified by
 * `tests/unit/lib/sitemap/utils.test.ts` and the golden test below.
 *
 * Also note: the old code never escaped `&`/`<`/`>` in `loc`/`lastmod`
 * text. XMLBuilder escapes automatically, which is a latent-bug fix (a
 * literal `&` in a path would have produced invalid XML before) rather than
 * a behavior regression — job/blog paths are slug-safe today so this never
 * fires in practice.
 */
const fragmentBuilder = new XMLBuilder({
	ignoreAttributes: false,
	attributeNamePrefix: "@_",
	format: true,
	indentBy: "  ",
	suppressEmptyNode: true,
});

type HreflangLink = {
	"@_rel": "alternate";
	"@_hreflang": string;
	"@_href": string;
};

/** Pure data builder shared by {@link buildHreflangLinks} and {@link buildUrlEntry}. */
function hreflangLinkObjects(
	path: string,
	alternateLocales?: readonly string[] | string[],
): HreflangLink[] | null {
	const locales = alternateLocales ?? supportedLocales;
	if (locales.length < 2) return null;
	const links: HreflangLink[] = locales.map((locale) => ({
		"@_rel": "alternate",
		"@_hreflang": locale,
		"@_href": `${BASE_URL}/${locale}${path}`,
	}));
	links.push({
		"@_rel": "alternate",
		"@_hreflang": "x-default",
		"@_href": `${BASE_URL}/${X_DEFAULT_LOCALE}${path}`,
	});
	return links;
}

/**
 * Regex **source** (not a RegExp) for the human-readable slug that precedes the
 * `--` delimiter in a job URL. {@link buildJobSlug} lowercases via `slugify`
 * and collapses every `--+` run, so the slug is lowercase and never itself
 * contains the delimiter.
 *
 * Underscore is in the class deliberately: `slugify` strips `[^\w-]`, and `\w`
 * INCLUDES `_`, so a title like `foo_bar` yields the slug `foo_bar`. The
 * round-trip property test found this with the counterexample
 * `["_", "_", null, "id"]` -> `.../jobs/_--_`. `_` is safe here because the
 * delimiter is `-`-based, so an underscore can never move the `lastIndexOf("--")`
 * split point.
 */
export const JOB_URL_SLUG_PATTERN = "[a-z0-9_-]+";

/**
 * Regex **source** (not a RegExp) for the job-id tail of a job URL — everything
 * after the final `--` in `/{locale}/jobs/{slug}--{jobId}`.
 *
 * ## Why this is a lookahead and not a plain character class
 *
 * `extractJobId()` in `src/app/[locale]/(public)/jobs/[slug]/page.tsx` parses
 * the id with `slug.lastIndexOf("--")`. This pattern is exactly the set of ids
 * that survive a `buildJobUrl()` → `extractJobId()` round-trip, which imposes
 * three constraints and no others:
 *
 *   1. non-empty;
 *   2. (HISTORICAL) no `--` inside the id. That was true while the route parsed
 *      with `lastIndexOf("--")`; since 2026-09-08 it parses with `indexOf`, so
 *      an id containing `--` survives the round-trip. See the note on
 *      `JOB_URL_ID_PATTERN` below;
 *   3. no *leading* hyphen — `{slug}--{-id}` renders as `slug---id`, whose last
 *      `--` sits one char earlier, so the id comes back missing its hyphen.
 *      (A *trailing* hyphen is fine and 3 real sources emit one.)
 *
 * Job ids are **not** restricted to `[A-Za-z0-9_]`. They are built per-source
 * as `job_<source>_<upstream token>` (see `src/lib/ingestion/*.ts`) and the
 * upstream token is frequently a slug or a UUID, so single hyphens are normal:
 *
 *   - `job_ashby_Render_b4a86799-59d4-4bf3-9681-23264550f1ea` (UUID)
 *   - `job_wwr_iterable-account-executive-enterprise-west-region` (slug)
 *   - `job_arbeitnow_remote-senior-account-executive-` (**trailing** hyphen)
 *
 * Of the 1,186 ids in `scripts/seed.sql`, 419 contain a hyphen and 3 end in
 * one. A `[A-Za-z0-9_]+` class rejected all 419 and broke the `@smoke` SEO
 * suite fleet-wide; `[A-Za-z0-9_]+(-[A-Za-z0-9_]+)*` still rejects the 3
 * trailing-hyphen ids. This lookahead form is the only one that matches all
 * 1,186 and agrees with `extractJobId()` on every one of them.
 *
 * It is still a real assertion: it pins the id to URL-path-safe characters, so
 * a space, `/`, `.`, `%` or an empty id — all signs of a URL-construction bug —
 * are rejected, as is the ambiguous `--`.
 *
 * **Assert against this constant. Never hand-write the character class.**
 */
/**
 * NOTE (2026-09-08): the `(?!--)` clause is now CONSERVATIVE, not a hard
 * constraint. `extractJobId()` splits on the FIRST `--` (the title half can
 * never contain one, because `slugify` collapses every `--+` run), so an id
 * containing `--` round-trips correctly — which it did not under the previous
 * `lastIndexOf` parse, where such an id was truncated to its tail and the page
 * 404'd. Five production rows carry one, all `job_careerjet_v2_*`.
 *
 * The pattern is left narrow because its only consumers are test assertions
 * over URLs we generate from fixtures that have no `--`, and a tighter
 * assertion there costs nothing. Widen it if a fixture ever needs to.
 */
export const JOB_URL_ID_PATTERN = "(?!-)(?:(?!--)[A-Za-z0-9_-])+";

/**
 * Regex source for a full canonical job-detail URL, e.g.
 * `https://lokerdollar.com/id/jobs/staff-designer-acme--job_adzuna_123`.
 * Unanchored — callers add `^`/`$` (or a trailing `(?:\?|$)`) to taste.
 */
export const JOB_URL_PATTERN = `https://lokerdollar\\.com/(?:en|id)/jobs/${JOB_URL_SLUG_PATTERN}--${JOB_URL_ID_PATTERN}`;

/** Regex source for the locale-prefixed job-detail path, without the origin. */
export const JOB_PATH_PATTERN = `/(?:en|id)/jobs/${JOB_URL_SLUG_PATTERN}--${JOB_URL_ID_PATTERN}`;

/**
 * Regex source for a full canonical apply (outbound) URL, e.g.
 * `https://lokerdollar.com/id/out/job_adzuna_123/staff-designer-acme`.
 *
 * Here the id is its own route segment (`[locale]/out/[jobId]/[[...slug]]`), so
 * there is no `--` delimiter to keep unambiguous — the id just has to be free
 * of `/`. Hyphens are still normal (same ingested ids as above), which is why
 * this is not `[A-Za-z0-9_]+` either.
 */
export const JOB_APPLY_URL_PATTERN = `https://lokerdollar\\.com/(?:en|id)/out/[A-Za-z0-9_-]+/${JOB_URL_SLUG_PATTERN}`;

/**
 * Build hreflang alternate links XML for a given path (without locale prefix).
 * Produces one <xhtml:link> per locale + x-default pointing to /id/.
 * Pass `alternateLocales` to restrict hreflang to a subset (e.g. id-only posts).
 * When only one locale is available, no hreflang links are emitted — Google
 * treats the page as monolingual, which is correct and avoids broken pairs.
 */
export function buildHreflangLinks(
	path: string,
	alternateLocales?: readonly string[] | string[],
): string {
	const links = hreflangLinkObjects(path, alternateLocales);
	if (!links) return "";
	return fragmentBuilder.build({ "xhtml:link": links }).trim();
}

/**
 * Build a full <url> entry with hreflang alternates.
 * Pass `alternateLocales` to restrict hreflang links (e.g. id-only blog posts).
 */
export function buildUrlEntry(opts: {
	locale: string;
	path: string;
	lastmod?: Date;
	alternateLocales?: readonly string[] | string[];
}): string {
	const { locale, path, lastmod, alternateLocales } = opts;
	const url: Record<string, unknown> = {
		loc: `${BASE_URL}/${locale}${path}`,
	};
	if (lastmod) url.lastmod = lastmod.toISOString();

	const links = hreflangLinkObjects(path, alternateLocales);
	if (links) url["xhtml:link"] = links;

	return fragmentBuilder.build({ url }).trim();
}

/**
 * Wrap <url> entries in a proper <urlset> with XML namespaces.
 */
export function wrapUrlset(entries: string[]): string {
	return [
		'<?xml version="1.0" encoding="UTF-8"?>',
		'<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"',
		'        xmlns:xhtml="http://www.w3.org/1999/xhtml">',
		...entries,
		"</urlset>",
	].join("\n");
}

/**
 * Build a sitemap index XML.
 */
export function buildSitemapIndex(
	sitemaps: { loc: string; lastmod?: Date }[],
): string {
	const entries = sitemaps.map((s) => {
		const sitemap: Record<string, unknown> = { loc: s.loc };
		if (s.lastmod) sitemap.lastmod = s.lastmod.toISOString();
		return fragmentBuilder.build({ sitemap }).trim();
	});

	return [
		'<?xml version="1.0" encoding="UTF-8"?>',
		'<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
		...entries,
		"</sitemapindex>",
	].join("\n");
}

/**
 * Build the slug for a job URL from its title and company.
 */
export function buildJobSlug(title: string, company: string | null): string {
	const titleSlug = slugify(title).replace(/--+/g, "-");
	const companySlug = company
		? `-${slugify(company).replace(/--+/g, "-")}`
		: "";
	const slug = `${titleSlug}${companySlug}`;
	// Never return an empty slug. `slugify` strips everything non-alphanumeric,
	// so a title that is blank, whitespace, or purely symbolic ("...", "!!!",
	// CJK punctuation) reduces to "" — and with no company that yields
	// `/{locale}/jobs/--{id}`, a URL whose slug segment is empty. That is
	// malformed on its face and violates the round-trip contract documented on
	// JOB_URL_ID_PATTERN above. The property test caught it with the shrunk
	// counterexample ["_", "", null, "id"] -> "https://lokerdollar.com/id/jobs/--_".
	// `extractJobId` still parses it (lastIndexOf("--") is position-based), so
	// this was never a 404 — it was a silently ugly canonical URL that could
	// reach a sitemap, an email, or a social card.
	return slug || "job";
}

/**
 * Build the canonical absolute URL for a job detail page.
 *
 * The route at `[locale]/(public)/jobs/[slug]/page.tsx` extracts the job id
 * from the slug by splitting on the literal `--` delimiter. A bare id without
 * the slug prefix (or a path without the `/{locale}` prefix) results in 404.
 *
 * ALWAYS use this helper when constructing job URLs that ship to users
 * (emails, GitHub PRs, social cards, push notifications, RSS, sitemaps, …).
 * Never hand-build `https://lokerdollar.com/jobs/{id}` strings.
 */
export function buildJobUrl(opts: {
	jobId: string;
	title: string;
	company: string | null;
	locale: string;
}): string {
	const { jobId, title, company, locale } = opts;
	const slug = buildJobSlug(title, company);
	return `${BASE_URL}/${locale}/jobs/${slug}--${publicJobId(jobId)}`;
}

/**
 * Build the canonical absolute URL for a job apply (outbound) page.
 * Mirrors the route at `[locale]/out/[jobId]/[[...slug]]`.
 */
export function buildJobApplyUrl(opts: {
	jobId: string;
	title: string;
	company: string | null;
	locale: string;
}): string {
	const { jobId, title, company, locale } = opts;
	const slug = buildJobSlug(title, company);
	return `${BASE_URL}/${locale}/out/${publicJobId(jobId)}/${slug}`;
}

export { isJobDetailPath } from "@/lib/jobs/is-job-detail-path";
export { BASE_URL, supportedLocales };

/** Max URLs per sitemap file (Google limit) */
export const SITEMAP_CHUNK_SIZE = 50_000;

/**
 * Guard against D1 rows where a millisecond epoch was written into a column
 * Drizzle's `timestamp` mode treats as seconds — the read multiplies by 1000
 * AGAIN, producing a date like +058387-04-06 (e.g. raw stored value
 * 1784293364423 → read as 1784293364423 * 1000 ms). Any lastmod more than 24h
 * in the future trips this guard.
 *
 * `d.getTime() / 1000` undoes the accidental extra *1000 and recovers the
 * ORIGINAL millisecond epoch the writer intended — a real, per-row-distinct
 * timestamp — instead of collapsing every corrupted row to today's date.
 * Falling back to a shared `new Date()` for every corrupted row is exactly
 * how the 2026-07-21 sitemap freshness audit found 1,094 sampled job URLs all
 * sharing one exact millisecond: every one of them hit this same fallback
 * inside the same synchronous request. Only truly unrecoverable values (the
 * division still lands outside the sane window) fall back to `new Date()`.
 */
export function sanitizeLastmod(d: Date | null | undefined): Date | undefined {
	if (!d) return undefined;
	const futureLimitMs = Date.now() + 86_400_000;
	if (d.getTime() <= futureLimitMs) return d;

	const recovered = new Date(d.getTime() / 1000);
	if (recovered.getTime() > 0 && recovered.getTime() <= futureLimitMs) {
		return recovered;
	}
	return new Date();
}
